import type { CreateReturnOrderDto } from 'washday-sdk';
import { resolveOrderLineCharge } from '@washday/utilities';
import { resolvePersistedSourceLineId } from './lineIdentity';

type ReturnSourceLine = {
  _id?: string;
  lineId?: string;
  orderProductLineID?: string;
  quantity?: number;
  qty?: number;
  type?: string;
  price?: number;
  expressPrice?: number;
};

type BuildCreateReturnOrderDtoInput = {
  sourceLines: ReturnSourceLine[];
  selectedQuantities: Record<string, number>;
  returnItemsChargeMode: 'no_charge' | 'charge';
  reason?: string;
  notes?: string;
};

export type OperationalReturnValidationErrorCode =
  | 'selectLines'
  | 'sourceLineMissing'
  | 'positiveQuantity'
  | 'wholeQuantity'
  | 'quantityExceedsSource';

export class OperationalReturnValidationError extends Error {
  constructor(public readonly code: OperationalReturnValidationErrorCode) {
    super(code);
    this.name = 'OperationalReturnValidationError';
  }
}

const optionalTrimmedValue = (value?: string): string | undefined => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

export const isOrderEligibleForOperationalReturn = (order: {
  orderType?: string | null;
}): boolean => (order.orderType ?? 'standard') === 'standard';

export const getInitialOperationalReturnQuantity = (
  line: ReturnSourceLine
): number | undefined => {
  const availableQuantity = Number(line.quantity ?? line.qty ?? 0);
  if (!Number.isFinite(availableQuantity) || availableQuantity <= 0) return undefined;

  if ((line.type ?? 'normal') === 'normal') {
    return availableQuantity >= 1 ? 1 : undefined;
  }

  return Math.min(1, availableQuantity);
};

export const getOperationalReturnNominalUnitPrice = (
  line: ReturnSourceLine,
  isExpress: boolean
): number => {
  const hasValidExpressPrice =
    line.expressPrice != null && Number.isFinite(Number(line.expressPrice));
  const shouldUseExpressPrice = isExpress && hasValidExpressPrice;

  return resolveOrderLineCharge(line, shouldUseExpressPrice).selectedUnitPrice;
};

export const buildCreateReturnOrderDto = ({
  sourceLines,
  selectedQuantities,
  returnItemsChargeMode,
  reason,
  notes
}: BuildCreateReturnOrderDtoInput): CreateReturnOrderDto => {
  const linesById = new Map(
    sourceLines
      .map((line) => [resolvePersistedSourceLineId(line), line] as const)
      .filter(([lineId]) => Boolean(lineId))
  );
  const selections = Object.entries(selectedQuantities).filter(([, quantity]) => quantity !== 0);

  if (selections.length === 0) {
    throw new OperationalReturnValidationError('selectLines');
  }

  const items = selections.map(([returnSourceOrderLineId, quantity]) => {
    const sourceLine = linesById.get(returnSourceOrderLineId);
    if (!sourceLine) {
      throw new OperationalReturnValidationError('sourceLineMissing');
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      throw new OperationalReturnValidationError('positiveQuantity');
    }
    if ((sourceLine.type ?? 'normal') === 'normal' && !Number.isInteger(quantity)) {
      throw new OperationalReturnValidationError('wholeQuantity');
    }

    const availableQuantity = Number(sourceLine.quantity ?? sourceLine.qty ?? 0);
    if (quantity > availableQuantity) {
      throw new OperationalReturnValidationError('quantityExceedsSource');
    }

    return { returnSourceOrderLineId, quantity };
  });

  return {
    items,
    returnItemsChargeMode,
    ...(optionalTrimmedValue(reason) ? { reason: optionalTrimmedValue(reason) } : {}),
    ...(optionalTrimmedValue(notes) ? { notes: optionalTrimmedValue(notes) } : {})
  };
};
